import Chip from "@mui/material/Chip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';

const Timeline = styled('div')(() => ({
  alignItems: 'flex-end',
  backgroundColor: 'rgba(255, 255, 255, 0.01)',
  backdropFilter: "blur(5px)",
  borderLeft: '1px solid rgba(192, 192, 192, 0.25)',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  height: '100%',
  minWidth: '50px',
  padding: '64px 4px 16px 8px',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  right: '20px',
  width: 'auto',
  zIndex: 1,
}));
const ChipColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-evenly',
  alignItems: 'flex-end',
  height: '100%',
  minHeight: 0,
  margin: 'auto 0',
  flex: 1,
}));
const DateChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: theme.palette.common.white,
  cursor: 'pointer',
  minWidth: 0,
  maxWidth: 'none',
  textOverflow: 'unset',
  pointerEvents: 'auto',
  whiteSpace: 'nowrap',
  width: 'auto',
  "&:hover": {
    backgroundColor: theme.palette.common.black,
  },
}));
const BackButton = styled(IconButton)(({ theme }) => ({
  alignSelf: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: theme.palette.common.white,
  pointerEvents: 'auto',
  "&:hover": {
    backgroundColor: theme.palette.common.black,
  },
}));
const CloseButton = styled(IconButton)(({ theme }) => ({
  alignSelf: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: theme.palette.common.white,
  pointerEvents: 'auto',
  "&:hover": {
    backgroundColor: theme.palette.common.black,
  },
}));

type TimeUnit = 'year' | 'month' | 'week' | 'day' | 'hour';

const Granularity: TimeUnit[] = [
  'year',
  'month',
  'week',
  'day',
  'hour'
];

const DateFormats: Record<TimeUnit, string> = {
  year: 'YYYY',
  month: 'MMM YYYY',
  week: '[W]WW YYYY',
  day: 'MMM D',
  hour: 'h A',
};

type HistoryState = {
  start: Moment;
  end: Moment;
  granularity: TimeUnit;
};

type TimelineProps = {
  className?: string;
  show: boolean;
  firstDate: Moment | undefined;
  lastDate: Moment | undefined;
  onDateSelected: (startDate?: string, endDate?: string) => void;
  onReset: () => void;
}

export default function TimelineOverlay(props: Readonly<TimelineProps>) {
  const {
    className,
    show,
    firstDate,
    lastDate,
    onDateSelected,
    onReset
  } = props;

  const [granularity, setGranularity] = useState<TimeUnit>(() =>
    pickInitialGranularity(firstDate, lastDate)
  );
  const [rangeStart, setRangeStart] = useState<Moment | undefined>(firstDate);
  const [rangeEnd, setRangeEnd] = useState<Moment | undefined>(lastDate);
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [isGoingBack, setIsGoingBack] = useState(false);

  useEffect(() => {
    if (history.length === 0) {
      setIsGoingBack(false);
    }
    if (!isGoingBack && show && firstDate && lastDate) {
      const initialGranularity = pickInitialGranularity(firstDate, lastDate);
      setGranularity(initialGranularity);
      setRangeStart(firstDate.clone());
      setRangeEnd(lastDate.clone());
    }
  }, [
    show,
    firstDate,
    lastDate
  ]);

  const dateChips = useMemo(() => {
    if (!show || !rangeStart || !rangeEnd) return [] as Moment[];
    return computeBuckets(rangeStart, rangeEnd, granularity);
  }, [show, rangeStart, rangeEnd, granularity]);

  function pickInitialGranularity(start?: Moment, end?: Moment): TimeUnit {
    if (!start || !end) return 'hour';
    if (end.diff(start, 'years') >= 1) return 'year';
    if (end.diff(start, 'months') >= 1) return 'month';
    if (end.diff(start, 'weeks') >= 1) return 'week';
    if (end.diff(start, 'days') >= 1) return 'day';
    return 'hour';
  }

  function nextGranularity(currentGranularity: TimeUnit): TimeUnit {
    const index = Granularity.indexOf(currentGranularity);
    return Granularity[Math.min(index + 1, Granularity.length - 1)];
  }

  function computeBuckets(
    rangeStart: Moment,
    rangeEnd: Moment,
    unit: TimeUnit
  ): Moment[] {
    const buckets: Moment[] = [];
    let cursor = rangeStart.clone().startOf(unit);
    const end = rangeEnd.clone();
    while (cursor.isSameOrBefore(end)) {
      buckets.push(cursor.clone());
      cursor.add(1, unit);
    }
    return buckets;
  }

  function handleChipClick(bucketStart: Moment) {
    setHistory(h => [
      ...h,
      {
        start: rangeStart!.clone(),
        end: rangeEnd!.clone(),
        granularity
      }
    ]);

    const newGranularity = nextGranularity(granularity);
    const bucketEnd = bucketStart.clone().endOf(granularity);

    setRangeStart(bucketStart);
    setRangeEnd(bucketEnd);
    setGranularity(newGranularity);

    onDateSelected(
      bucketStart.toISOString(),
      bucketEnd.toISOString()
    );
  }

  function handleBackButtonClick() {
    setHistory(h => {
      if (h.length === 0) return h;

      const newHistory = [...h];
      const last = newHistory.pop()!;

      setRangeStart(last.start);
      setRangeEnd(last.end);
      setGranularity(last.granularity);
      setIsGoingBack(true);

      if (h.length === 1) {
        onReset();
      } else {
        onDateSelected(
          last.start.toISOString(),
          last.end.toISOString()
        );
      }

      return newHistory;
    });
  }

  function getLabelFormat(granularity: TimeUnit): string {
    return DateFormats[granularity] ?? DateFormats.hour;
  }

  if (!show) return null;

  return (
    <ClickAwayListener onClickAway={() => onDateSelected(undefined, undefined)}>
      <Timeline className={className}>
        {history.length === 0 ? (
          <Tooltip title='Close'>
            <CloseButton
              onClick={() => onDateSelected(undefined, undefined)}
            >
              <CloseIcon />
            </CloseButton>
          </Tooltip>
        ) : (
          <Tooltip title='Back'>
            <BackButton
              onClick={handleBackButtonClick}
            >
              <ArrowBackIcon />
            </BackButton>
          </Tooltip>
        )}
        <ChipColumn>
          {dateChips.map((date: Moment) => {
            return (
              <DateChip
                key={date.toISOString()}
                disabled={dateChips.length === 1}
                label={date.format(getLabelFormat(granularity))}
                size='small'
                onMouseUp={() => handleChipClick(date)}
              />
            );
          })}
        </ChipColumn>
      </Timeline>
    </ClickAwayListener>
  );
}
